// =============================================================
// noteCollab.ts — the Notes live co-editing CLIENT, shared by the web app
// (frontend/src/notebook/collab.js) and the iOS editor (the Expo DOM
// component mobile/src/notebook/NotebookEditor.tsx). One copy, so the two
// clients cannot disagree about the protocol. Server:
// api/src/functions/notebookCollab.js (migration 233).
//
// It takes its dependencies rather than importing them — Yjs, the awareness
// helpers, how to make a request, how to build a seed, and (iOS) how to
// persist — because @harvus/shared depends on nothing.
//
//   * THE LOG IS THE AUTHORITY. Local edits are merged and POSTed (one in
//     flight); a push is applied only when it is the NEXT seq, anything else
//     reads the log. Without Web PubSub the log is polled.
//   * OFFLINE (iOS): with `persist`, the document and every unacknowledged
//     update are written to the device after each change. Opened offline, the
//     note edits from that copy; on reconnect the unsent updates are POSTed
//     and the log is read, and Yjs merges — no conflict prompt. The one case
//     that cannot merge is a GENERATION change (a whole-document writer
//     replaced the note): `onGenerationLost` hands that to the caller.
//   * Content (search, snippets, legacy readers) is snapshotted by the tab
//     that edited, after its own updates are acknowledged.
// =============================================================

export const COLLAB_FRAGMENT = "document-store";
const SEND_DEBOUNCE_MS = 150;
const SNAPSHOT_DELAY_MS = 2000;
const PERSIST_DELAY_MS = 300;
const RETRY_MS = 5000;
const SEED_WAIT_MS = 1000;
const SEED_MAX_WAITS = 45;

export interface CollabResponse { ok: boolean; status: number; body: any; network: boolean }
export type CollabRequest = (method: "GET" | "POST" | "PUT", path: string, body?: unknown) => Promise<CollabResponse>;

export interface CollabRecord {
  noteId: string;
  generation: number;
  lastSeq: number;
  clientRef: string;
  /** The whole local document (base64 Yjs update), unsent edits included. */
  doc: string | null;
  /** Server pieces fetched but not yet applied (a prefetch while the note was closed). */
  parts: string[];
  /** Local updates the server has not acknowledged, in order. */
  unsent: string[];
  me?: { id: string; name: string } | null;
  updatedAt: number;
}

export interface CollabStatus { connection: "live" | "polling" | "offline"; pending: boolean; error: string | null }

export interface CollabDeps {
  Y: any;
  awareness: { Awareness: any; encodeAwarenessUpdate: any; applyAwarenessUpdate: any; removeAwarenessStates: any };
  request: CollabRequest;
  /** Convert stored Content (BlockNote JSON or legacy HTML) into a base64 Yjs update. */
  buildSeed: (content: string) => Promise<string>;
  persist?: { load: () => Promise<CollabRecord | null>; save: (r: CollabRecord) => Promise<void> };
  WebSocketImpl?: any;
}

// ── Pure rules (unit-tested) ──

export function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(bin);
}

export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** A random per-device id the server records beside each update (never a UUID column). */
export function newCollabClientRef(): string {
  const a = new Uint8Array(12);
  (globalThis as any).crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 24);
}

/**
 * What to do with a pushed update. Only the NEXT seq is applied from a push;
 * anything further ahead means one was missed, so the log is read instead.
 */
export function collabPushAction({ seq, lastSeq, hasUpdate }: { seq: number; lastSeq: number; hasUpdate: boolean }): "apply" | "ignore" | "catchup" {
  if (!Number.isFinite(seq)) return "catchup";
  if (seq <= lastSeq) return "ignore";
  if (seq === lastSeq + 1 && hasUpdate) return "apply";
  return "catchup";
}

/**
 * Document content is only ever taken from a SERVER message (an update that
 * was persisted). A peer can send to the group too — that is how cursors
 * travel — but an update that never reached the log would make one copy
 * diverge from everybody else's.
 */
export const isCollabServerMessage = (m: any) => !!m && m.type === "message" && m.from === "server";
export const isCollabPeerMessage = (m: any) => !!m && m.type === "message" && m.from === "group";

const CURSOR_COLORS = ["#d9480f", "#1971c2", "#2f9e44", "#9c36b5", "#e67700", "#0c8599", "#c2255c", "#5f3dc4"];
export function collabColorFor(id: string): string {
  const s = String(id || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CURSOR_COLORS[h % CURSOR_COLORS.length];
}

export function collabStatusLabel(st: Partial<CollabStatus> | null | undefined): string {
  if (!st) return "Saved";
  if (st.error) return st.error;
  if (st.connection === "offline") return st.pending ? "Offline — saved on this device" : "Offline";
  if (st.pending) return "Saving…";
  if (st.connection === "live") return "Live · Saved";
  return "Saved";
}

/**
 * Should a persisted record be used, discarded, or is it unrecoverable? A
 * record from an older generation can be dropped when it holds nothing unsent;
 * with unsent edits it cannot be merged into the new document.
 */
export function reconcileRecord(record: CollabRecord | null, serverGeneration: number | null): "use" | "discard" | "lost" | "none" {
  if (!record) return "none";
  if (serverGeneration === null || record.generation === serverGeneration) return "use";
  return record.unsent.length ? "lost" : "discard";
}

// ── The provider ──

export interface CollabProvider {
  doc: any;
  fragment: any;
  awareness: any;
  user: { name: string; color: string };
  provider: { awareness: any };
  setContentSource: (fn: () => string) => void;
  destroy: () => Promise<void>;
}

export interface OpenCollabArgs {
  noteId: string;
  deps: CollabDeps;
  onStatus?: (s: CollabStatus) => void;
  /** Another writer replaced the note wholesale: reload from Content. */
  onReset?: () => void;
  /** Same, but this device holds edits that cannot be merged into it. */
  onGenerationLost?: () => void;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Returns null when this note cannot be co-edited here (pre-233 server, the
 * note is not on the server yet, or offline with no local copy of the live
 * document) — the caller then edits it as a whole document.
 */
export async function openNoteCollab({ noteId, deps, onStatus, onReset, onGenerationLost }: OpenCollabArgs): Promise<CollabProvider | null> {
  const { Y, request } = deps;
  const A = deps.awareness;
  const base = `notebook/${noteId}/collab`;
  const REMOTE = "remote";

  let record = deps.persist ? await deps.persist.load().catch(() => null) : null;
  let first = await request("GET", base);
  let online = !first.network;

  if (!online) {
    if (!record || (!record.doc && !record.parts.length)) return null;
  } else {
    if (!first.ok || !first.body?.available) return null;
    const verdict = reconcileRecord(record, Number(first.body.generation));
    if (verdict === "lost") { onGenerationLost?.(); return null; }
    if (verdict === "discard") record = null;
    if (!first.body.seeded) {
      if (record?.unsent.length) { onGenerationLost?.(); return null; }
      record = null;
      const seeded = await seed(first);
      if (!seeded) return null;
      first = seeded;
    }
  }

  async function seed(start: CollabResponse): Promise<CollabResponse | null> {
    const clientRef = newCollabClientRef();
    let st = start;
    for (let i = 0; i < SEED_MAX_WAITS; i++) {
      if (st.body?.seeded) return st;
      const claim = await request("POST", `${base}/claim`, { clientId: clientRef });
      if (!claim.ok) return null;
      if (claim.body.claimed) {
        const state = await deps.buildSeed(claim.body.content || "");
        const res = await request("POST", `${base}/seed`, { clientId: clientRef, generation: claim.body.generation, state });
        if (!res.ok && res.status !== 409) return null; // 409: another device won
      } else {
        await sleep(SEED_WAIT_MS); // another device is converting it
      }
      st = await request("GET", base);
      if (!st.ok) return null;
    }
    return null;
  }

  const doc = new Y.Doc();
  const fragment = doc.getXmlFragment(COLLAB_FRAGMENT);
  const awareness = new A.Awareness(doc);
  const me = (online ? first.body.me : null) || record?.me || { id: "me", name: "Me" };
  const user = { name: me.name, color: collabColorFor(me.id) };
  const clientRef = record?.clientRef || newCollabClientRef();

  let generation: number = online ? Number(first.body.generation) : record!.generation;
  let lastSeq = record?.lastSeq ?? 0;
  let haveBase = false;
  let destroyed = false;
  let resetFired = false;
  let pending: Uint8Array[] = [];
  let inflight: Uint8Array | null = null;
  let sendTimer: any = null;
  let snapTimer: any = null;
  let persistTimer: any = null;
  let retryTimer: any = null;
  let pollTimer: any = null;
  let socket: any = null;
  let socketTimer: any = null;
  let group: string | null = null;
  let localDirty = false;
  let getContent: (() => string) | null = null;
  let connection: CollabStatus["connection"] = online ? "polling" : "offline";
  let error: string | null = null;
  let catchingUp: Promise<void> | null = null;

  const status = () => onStatus?.({ connection, pending: pending.length > 0 || !!inflight, error });
  const applyRemote = (b64: string) => Y.applyUpdate(doc, b64ToBytes(b64), REMOTE);

  const reset = (lost: boolean) => {
    if (resetFired || destroyed) return;
    resetFired = true;
    if (lost) onGenerationLost?.(); else onReset?.();
  };
  const unsentCount = () => pending.length + (inflight ? 1 : 0);

  // ── Load what this device holds ──
  if (record) {
    if (record.doc) applyRemote(record.doc);
    for (const p of record.parts) applyRemote(p);
    for (const u of record.unsent) { applyRemote(u); pending.push(b64ToBytes(u)); }
    haveBase = true;
  }

  const persistNow = async () => {
    if (!deps.persist) return;
    const unsent = [...(inflight ? [inflight] : []), ...pending].map(bytesToB64);
    await deps.persist.save({
      noteId, generation, lastSeq, clientRef, me,
      doc: bytesToB64(Y.encodeStateAsUpdate(doc)), parts: [], unsent, updatedAt: Date.now(),
    }).catch(() => {});
  };
  const schedulePersist = () => {
    if (!deps.persist || destroyed) return;
    clearTimeout(persistTimer);
    persistTimer = setTimeout(persistNow, PERSIST_DELAY_MS);
  };

  const goOffline = () => {
    if (connection !== "offline") { connection = "offline"; status(); }
    clearTimeout(retryTimer);
    retryTimer = setTimeout(goOnline, RETRY_MS);
  };

  // Read the log from lastSeq (or the whole state when we hold none).
  const catchUp = (): Promise<void> => {
    if (catchingUp) return catchingUp;
    catchingUp = (async () => {
      try {
        for (let guard = 0; guard < 20; guard++) {
          const res = await request("GET", haveBase ? `${base}?since=${lastSeq}` : base);
          if (res.network) { goOffline(); return; }
          if (!res.ok) { if (res.status === 404) { error = "This note is no longer available."; status(); } return; }
          const b = res.body;
          if (Number(b.generation) !== generation || !b.seeded) { reset(unsentCount() > 0); return; }
          if (b.state) applyRemote(b.state);
          for (const u of b.updates || []) applyRemote(u.update);
          lastSeq = Math.max(lastSeq, Number(b.throughSeq ?? lastSeq));
          haveBase = true;
          if (!b.more) break;
        }
        schedulePersist();
      } finally {
        catchingUp = null;
      }
    })();
    return catchingUp;
  };

  const send = async (): Promise<void> => {
    if (inflight || destroyed || connection === "offline") return;
    if (!pending.length) { status(); return; }
    inflight = pending.length === 1 ? pending[0] : Y.mergeUpdates(pending);
    pending = [];
    status();
    const res = await request("POST", `${base}/updates`, { clientId: clientRef, generation, update: bytesToB64(inflight!) });
    if (res.network || (!res.ok && res.status >= 500)) {
      pending = [inflight!, ...pending];
      inflight = null;
      schedulePersist();
      if (res.network) goOffline();
      else { clearTimeout(retryTimer); retryTimer = setTimeout(send, RETRY_MS); }
      status();
      return;
    }
    if (!res.ok) {
      if (res.status === 409) { reset(true); return; }
      error = res.body?.error || "This change could not be saved.";
      inflight = null;
      status();
      return;
    }
    inflight = null;
    const seq = Number(res.body.seq);
    if (seq === lastSeq + 1) lastSeq = seq;
    else if (seq > lastSeq) await catchUp(); // others wrote in between
    schedulePersist();
    if (pending.length) send(); else status();
  };

  async function goOnline(): Promise<void> {
    if (destroyed) return;
    const res = await request("GET", haveBase ? `${base}?since=${lastSeq}` : base);
    if (res.network) { goOffline(); return; }
    if (!res.ok) return;
    if (Number(res.body.generation) !== generation || !res.body.seeded) { reset(unsentCount() > 0); return; }
    connection = socket ? "live" : "polling";
    status();
    await catchUp();
    await send();
    if (localDirty) scheduleSnapshot();
    if (!socket) connect();
  }

  function scheduleSnapshot() {
    clearTimeout(snapTimer);
    snapTimer = setTimeout(snapshot, SNAPSHOT_DELAY_MS);
  }
  async function snapshot(): Promise<void> {
    if (!localDirty || !getContent || destroyed) return;
    if (unsentCount() || connection === "offline") { if (connection !== "offline") scheduleSnapshot(); return; }
    localDirty = false;
    const res = await request("PUT", `notebook/${noteId}`, { collab: true, content: getContent(), generation, seq: lastSeq });
    if (!res.ok) { localDirty = true; if (!res.network) scheduleSnapshot(); }
  }

  doc.on("update", (u: Uint8Array, origin: unknown) => {
    if (destroyed) return;
    schedulePersist();
    if (origin === REMOTE) return;
    pending.push(u);
    localDirty = true;
    status();
    clearTimeout(sendTimer);
    sendTimer = setTimeout(send, SEND_DEBOUNCE_MS);
    scheduleSnapshot();
  });

  // ── Web PubSub ──
  const sendJson = (obj: unknown) => { if (socket?.readyState === 1) socket.send(JSON.stringify(obj)); };
  const broadcastAwareness = (clients: number[]) => {
    if (!group) return;
    sendJson({ type: "sendToGroup", group, dataType: "json", noEcho: true,
      data: { type: "awareness", update: bytesToB64(A.encodeAwarenessUpdate(awareness, clients)) } });
  };
  awareness.on("update", ({ added, updated, removed }: any, origin: unknown) => {
    if (origin === REMOTE) return;
    broadcastAwareness([...added, ...updated, ...removed]);
  });

  const onMessage = (ev: { data: string }) => {
    let m: any;
    try { m = JSON.parse(ev.data); } catch { return; }
    if (isCollabServerMessage(m)) {
      const d = m.data || {};
      if (d.generation !== undefined && Number(d.generation) !== generation) { reset(unsentCount() > 0); return; }
      if (d.type === "update") {
        if (d.clientId === clientRef) return; // ours; the POST response counted it
        const action = collabPushAction({ seq: Number(d.seq), lastSeq, hasUpdate: !!d.update });
        if (action === "apply") { applyRemote(d.update); lastSeq = Number(d.seq); }
        else if (action === "catchup") catchUp();
      }
    } else if (isCollabPeerMessage(m)) {
      const d = m.data || {};
      if (d.type === "awareness" && d.update) {
        try { A.applyAwarenessUpdate(awareness, b64ToBytes(d.update), REMOTE); } catch { /* a bad peer message */ }
      } else if (d.type === "hello") {
        broadcastAwareness([doc.clientID]);
      }
    }
  };

  async function connect(attempt = 0): Promise<void> {
    const WS = deps.WebSocketImpl || (globalThis as any).WebSocket;
    if (destroyed || !WS || connection === "offline") return;
    const tok = await request("POST", `${base}/token`, {});
    if (destroyed) return;
    if (tok.network) { goOffline(); return; }
    if (!tok.ok || !tok.body?.available) return; // polling only
    group = tok.body.group;
    const ws = new WS(tok.body.url, "json.webpubsub.azure.v1");
    socket = ws;
    ws.onopen = () => {
      connection = "live";
      status();
      sendJson({ type: "sendToGroup", group, dataType: "json", noEcho: true, data: { type: "hello" } });
      broadcastAwareness([doc.clientID]);
      catchUp(); // anything written while the socket was down
    };
    ws.onmessage = onMessage;
    ws.onclose = () => {
      if (socket === ws) socket = null;
      if (destroyed) return;
      if (connection === "live") connection = "polling";
      status();
      socketTimer = setTimeout(() => connect(attempt + 1), Math.min(30000, 1000 * 2 ** attempt));
    };
  }

  awareness.setLocalStateField("user", user);

  if (online) {
    // A device that held a copy reads only what it missed; one that held none
    // reads the state and the log.
    if (!record) {
      const b = first.body;
      if (b.state) applyRemote(b.state);
      for (const u of b.updates || []) applyRemote(u.update);
      lastSeq = Number(b.throughSeq ?? 0);
      haveBase = true;
      if (b.more) await catchUp();
    } else {
      await catchUp();
    }
    send();
    connect();
  } else {
    retryTimer = setTimeout(goOnline, RETRY_MS);
  }
  pollTimer = setInterval(() => { if (!destroyed && connection !== "offline") catchUp(); }, (online && first.body.pollMs) || 2500);
  schedulePersist();
  status();

  return {
    doc, fragment, awareness, user,
    provider: { awareness },
    setContentSource(fn) { getContent = fn; },
    async destroy() {
      destroyed = true;
      clearTimeout(sendTimer); clearTimeout(socketTimer); clearTimeout(retryTimer); clearInterval(pollTimer);
      clearTimeout(persistTimer);
      // Try once to deliver what is unsent; whatever is left stays persisted.
      if (pending.length && !inflight && connection !== "offline") {
        const update = pending.length === 1 ? pending[0] : Y.mergeUpdates(pending);
        const res = await request("POST", `${base}/updates`, { clientId: clientRef, generation, update: bytesToB64(update) });
        if (res.ok) { pending = []; } else { pending = [update]; }
      }
      clearTimeout(snapTimer);
      if (localDirty && getContent && !unsentCount() && connection !== "offline") {
        await request("PUT", `notebook/${noteId}`, { collab: true, content: getContent(), generation, seq: lastSeq });
      }
      await persistNow();
      try { A.removeAwarenessStates(awareness, [doc.clientID], "local"); } catch { /* ignore */ }
      try { socket?.close(); } catch { /* ignore */ }
      awareness.destroy();
      doc.destroy();
    },
  };
}
